import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ScrollView, StyleSheet, TextInput as RNTextInput, View, useWindowDimensions } from 'react-native';
import { Link, RelativePathString, useRouter } from 'expo-router';
import Head from 'expo-router/head';
import * as AppUtils from '@/utilities/AppUtils';
import { CustomText, CustomTextStyles } from '@/components/CustomText';
import { Button, Checkbox, Fieldset, Hourglass, Panel, Radio, TextInput } from 'react95-native-rabbl';
import { AppText } from '@/components/AppText';
import { CustomDivider } from "@/components/CustomDivider";
import * as AppConstants from '@/utilities/AppConstants';
import * as AppConsts from '@/utilities/AppConsts';
import { AppContext } from '@/components/AppContext';
import * as AppUtilities from "@/utilities/AppUtilities";
import Footer from '@/components/Footer';
import type { RootState } from '@/state/store';
import { useSelector, useDispatch } from 'react-redux';
import { setLoggedIn } from '@/state/user';
import { setData } from '@/state/user';
import { CommunityMembership, PaymentStatus } from "@/types/community_membership";
import { UNKNOWN_USER_INFO, UserInfo } from '@/types/user_info';
import { Community, UNKNOWN_COMMUNITY } from '@/types/community';
import { Gender } from '@/types/user_info';
import {
  finishUpload,
  startUpload,
  removeUpload,
  updateVisuals,
  updateUploadProgress,
  FinishUploadUpdate,
  Visual,
  UploadProgress,
  UploadProgressUpdate,
} from '@/state/uploads';
import Purchases, { LOG_LEVEL, LogInResult, MakePurchaseResult, PurchasesOffering, PurchasesOfferings } from 'react-native-purchases';
import Constants from 'expo-constants';
import SubdomainHomepage from '@/components/SubdomainHomepage';
import { CreditCardInput, CreditCardFormData } from "react-native-credit-card-input";
import { HumanVerifier } from '@/components/HumanVerifier';
import { HUMAN_VERIFIER_NON_WEB_SUCCESS, IHumanVerifier } from '@/types/human_verifier_props';
import { GooglePlacesAutocomplete, GooglePlacesAutocompleteRef, Place } from '@/components/react-native-google-places-autocomplete/GooglePlacesAutocomplete';
//import { GooglePlacesAutocomplete, GooglePlacesAutocompleteRef, Place } from 'react-native-google-places-autocomplete';
import { useHeaderHeight } from "expo-router/react-navigation";
import { useForm, Controller } from "react-hook-form";
import { Image } from 'expo-image';
import { ExternalLink } from '@/components/ExternalLink';
import { md5 } from 'js-md5';

const MIN_LIST_HEIGHT = 150;
const NEEDS_BILLING = false;
const USE_VERIFIER = false;

const CREDIT_CARD_INPUT_NOT_AVAILABLE = AppConsts.PAYMENTS_LINE1;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
    alignItems: 'center',
    backgroundColor: 'transparent',
    gap: 20,
  },
  contentWrapper: {
    padding: 20,
    gap: 20,
  },
  inputWrapper: {
    gap: 10,
    alignItems: 'center',
    minHeight: MIN_LIST_HEIGHT,
  },
  billingAddressWrapper: {
    gap: 20,
    width: "100%",
  },
  formElement: {
    width: "100%",
  },
  addressFormWrapper: {
    gap: 20,
    alignItems: "center",
  },
  codeInput: {
    width: AppConsts.BASIC_MINIMUM_WIDTH - 50,
  },
  appStoreImage: {
    width: 174,
    height: 59,
  },
  playStoreImage: {
    width: 174,
    height: 52,
  },
  simpleWrapper: {
    gap: 20,
    width: 250,
  },
  button: {
    alignSelf: "center",
    width: 200,
  },
});

export enum PaymentMethod {
  UNKNOWN = "",
  GOOGLE_IAP = "googleiap",
  APPLE_IAP = "appleiap",
  CREDIT_CARD = "creditcard",
};

let DEFAULT_PAYMENT_METHOD = PaymentMethod.UNKNOWN;
if (AppUtilities.isIOS()) {
  DEFAULT_PAYMENT_METHOD = PaymentMethod.APPLE_IAP;
} else if (AppUtilities.isAndroid()) {
  DEFAULT_PAYMENT_METHOD = PaymentMethod.GOOGLE_IAP;
} else if (AppUtilities.isWeb()) {
  DEFAULT_PAYMENT_METHOD = PaymentMethod.CREDIT_CARD;
}

function checkFree(community: Community, gender: Gender, verifiedDiscountFree: boolean): boolean {
  if (verifiedDiscountFree) {
    return true;
  }
  return AppUtils.isValidCommunity(community) && AppUtils.isCommunityFree(community, gender);
}

function isDiscountCodeSandbox(code: string): boolean {
  return code == "sandbox";
}

interface DiscountProps {
  gender: Gender,
  onSuccess: (code: string, newpricefree: boolean) => void,
};

function Discount({ gender, onSuccess } : DiscountProps) : React.ReactNode {
  const [code, setCode] = useState("");
  const { showSpinner, hideSpinner } = useContext(AppContext);
  const domain = useSelector((state: RootState) => state.user.domain);

  const onSubmit = useCallback(async () => {
    if (code && code.length > 0) {
      if (!isDiscountCodeSandbox(code)) {
        try {
          showSpinner();

          const response = await AppUtilities.fetchUnauthorized(
            "JoinAndPay.discount",
            "/rabbl/api/discount",
            "POST",
            {
              domain: domain,
              code: code,
              gender: gender,
            }
          );

          const newpricefree: boolean = ((response as any).body as any).newpricefree;
          AppUtilities.info("Discount response: newpricefree: " + newpricefree);
          onSuccess(code, newpricefree);
          setCode("");
          AppUtils.showInformationalToast("Successfully applied discount");

        } catch (e) {
          AppUtils.showErrorToast(AppUtilities.getErrors(e), () => onSubmit(), true);
          AppUtilities.handleException(e, "JoinAndPay.discount");
        } finally {
          hideSpinner();
        }
      } else {
        AppUtilities.info("Discount is sandbox discount code");
        onSuccess(code, false);
        setCode("");
        AppUtils.showInformationalToast("Switched to sandbox mode");
      }
    }
  }, [code, domain]);

  return (
    <View style={{ flexDirection: "row", alignItems: "center", gap: 5 }}>
      <TextInput
        value={code}
        onChangeText={setCode}
        placeholder="Discount code"
        returnKeyType="next"
        autoComplete="off"
        onSubmitEditing={onSubmit}
        style={styles.codeInput}
      />
      <Button
        onPress={onSubmit}
        disabled={code == ""}
      >Apply</Button>
    </View>
  );
}

interface JoinAndPayProps {
  autoRedirectForLoggedInUser?: boolean,
};

// If a user completes the JoinAndPay flow through either
// a free join or by paying through an in-app payment (but not
// through a web credit card payment), then there's a communityMembership
// useEffect that detects that the membership is now valid and
// redirects to the main logged-in tabs. However, under certain flows,
// despite calling router.replace, JoinAndPay might stay mounted and then
// under certain conditions when the communityMembership state is updated
// while the user is doing something or navigating, the useEffect of this
// mounted component gets executed again and re-routes the user to the
// homepage. Unclear how to effectively unmount this component for this
// flow, so what we do is we have a global map from the communityMembership
// uniqueid to a boolean stating whether we've already done the redirect
// and thus avoid doing subsequent such redirects.
const redirectedCommunityMemberships = new Map<string, boolean>();

export default function JoinAndPay({
  autoRedirectForLoggedInUser = true,
}: JoinAndPayProps) : React.ReactNode {
  const domain = useSelector((state: RootState) => state.user.domain);
  const loggedIn = useSelector((state: RootState) => state.user.loggedIn);
  const community = useSelector((state: RootState) => state.user.current_community);
  const communityMembership = useSelector((state: RootState) => state.user.communityMembership);
  const user_info = useSelector((state: RootState) => state.user.user_info);
  const [paymentMethod, setPaymentMethod] = useState<string>(DEFAULT_PAYMENT_METHOD);
  const [gender, setGender] = useState<Gender>(Gender.GENDER_UNKNOWN);
  const { showSpinner, hideSpinner } = useContext(AppContext);
  const router = useRouter();
  const [processing, setProcessing] = useState(false);
  const dispatch = useDispatch();
  const [error, setError] = useState('');
  const [offering, setOffering] = useState<PurchasesOffering | undefined>();
  const [creditCard, setCreditCard] = useState<CreditCardFormData | undefined>(undefined);
  const [joinErrors, setJoinErrors] = useState<string[]>([]);
  const [humanVerifierToken, setHumanVerifierToken] = useState<string>("");
  const humanVerifier = useRef<IHumanVerifier>(null);
  const [verifiedDiscountCode, setVerifiedDiscountCode] = useState("");
  const [verifiedDiscountFree, setVerifiedDiscountFree] = useState(false);
  const [invitecode, setInvitecode] = useState("");
  const { width: screenWidth, height: screenHeight } = useWindowDimensions();

  useEffect(() => {
    if (AppUtils.isValidUserInfoGender(user_info)) {
      setGender(user_info.gender);
    }
  }, [user_info]);

  useEffect(() => {

    const isValidCommunityMembership = AppUtils.isValidCommunityMembership(communityMembership);

    AppUtilities.info("JoinAndPay communityMembership valid: " + isValidCommunityMembership + ", payment status: " + (communityMembership.payment_status ?? -1));

    if (autoRedirectForLoggedInUser && isValidCommunityMembership) {
      // See comment on redirectedCommunityMemberships above
      if (!redirectedCommunityMemberships.getOrInsert(communityMembership.uniqueid, false)) {
        const nextPage = AppUtils.getProfileCreateNextPageLink(communityMembership);
        if (nextPage != "/pay") {
          AppUtilities.info("JoinAndPay useEffect redirecting logged in user to " + nextPage);
          redirectedCommunityMemberships.set(communityMembership.uniqueid, true);
          router.replace(nextPage as RelativePathString);
        } else {
          AppUtilities.info("JoinAndPay useEffect skipping redirect");
        }
      }
    }
  }, [communityMembership, autoRedirectForLoggedInUser]);

  const buttons = useCallback(() => {
    return (
      <View style={AppConstants.STYLES.gap20alignCenter}>
        <AppText>Create an account or login to continue:</AppText>
        <View style={styles.button}>
          <Link href="/signup" asChild={true} className={"nontext"}>
            <Button>Create account</Button>
          </Link>
        </View>
        <View style={styles.button}>
          <Link href="/signin" asChild={true} className={"nontext"}>
            <Button>Login</Button>
          </Link>
        </View>
      </View>
    );
  }, []);

  const processFreeOrPayment = useCallback(
    async () => {
      let runFinally = true;
      try {
        setError('');
        const free = checkFree(community, gender, verifiedDiscountFree);

        AppUtilities.info("JoinAndPay.processFreeOrPayment free: " + free);

        const newJoinErrors: string[] = [];
        if (USE_VERIFIER && humanVerifierToken.length == 0) {
          newJoinErrors.push("Please complete the challenge");
        }
        if (!free && paymentMethod == PaymentMethod.CREDIT_CARD) {
          if (!community.wpe && !isDiscountCodeSandbox(verifiedDiscountCode)) {
            newJoinErrors.push(CREDIT_CARD_INPUT_NOT_AVAILABLE);
            setJoinErrors(newJoinErrors);
            return;
          }
          /*if (!isDiscountCodeSandbox(verifiedDiscountCode)) {
            if (creditCard) {
              if (!creditCard.valid) {
                if (creditCard.status.number == "invalid") {
                  newJoinErrors.push("Credit card number invalid");
                } else if (creditCard.status.number == "incomplete") {
                  newJoinErrors.push("Credit card number incomplete");
                }
                if (creditCard.status.expiry == "invalid") {
                  newJoinErrors.push("Expiration date invalid");
                } else if (creditCard.status.expiry == "incomplete") {
                  newJoinErrors.push("Expiration date incomplete");
                }
                if (creditCard.status.cvc == "invalid") {
                  newJoinErrors.push("CVC/CVV invalid");
                } else if (creditCard.status.cvc == "incomplete") {
                  newJoinErrors.push("CVC/CVV incomplete");
                }
              }
            } else {
              newJoinErrors.push(CREDIT_CARD_INPUT_NOT_AVAILABLE);
            }
            if (!creditCard?.valid) {
              setJoinErrors(newJoinErrors);
              return;
            }
          }*/
        }

        // Reset any previous errors
        setJoinErrors(newJoinErrors);

        if (!processing) {

          if (community.inviteonly && invitecode.length == 0) {
            throw new Error("Invite code required");
          }

          setProcessing(true);
          showSpinner();

          // Only use the human verifier on web. On non-web, the verifier will set the token to
          // HUMAN_VERIFIER_NON_WEB_SUCCESS.

          AppUtilities.info("JoinAndPay.processFreeOrPayment started processing");

          if (USE_VERIFIER && humanVerifierToken.length > 0 && humanVerifierToken != HUMAN_VERIFIER_NON_WEB_SUCCESS) {
            // This will throw an error if there's a failure
            AppUtilities.info("JoinAndPay.processFreeOrPayment checking human vertification");
            
            await AppUtilities.fetchUnauthorized(
              "JoinAndPay.processFreeOrPayment",
              "/rabbl/api/turnstile",
              "POST",
              {
                token: humanVerifierToken,
              }
            );
          }

          const joinpayData = {
            domain: domain,
            paymentMethod: paymentMethod,
            credit_card: creditCard?.values,
            verifiedDiscountFree: verifiedDiscountFree,
            verifiedDiscountCode: verifiedDiscountCode,
            gender: gender,
          };

          if (AppUtils.isIOSFree() && AppUtilities.isIOS()) {
            (joinpayData as any).utm = "ios";
          }

          AppUtilities.info("JoinAndPay.processFreeOrPayment calling joinpay");

          const response2 = await AppUtilities.fetchAuthorized(
            "JoinAndPay.processFreeOrPayment",
            "/rabbl/api/joinpay",
            "POST",
            joinpayData,
          );

          const uinfo: UserInfo = ((response2 as any).body as any).user_info;
          const isFullyPaid: boolean = ((response2 as any).body as any).isFullyPaid;
          dispatch(setData(response2));
          dispatch(updateVisuals([]));

          if (isFullyPaid) {
            // The dispatch above will update communityMembership and there's another useEffect that does a redirect
            AppUtilities.info("JoinAndPay.processFreeOrPayment isFullyPaid");
          } else {
            // Depending on the payment type we figure out the next action
            const offeringPriceUSD = gender == Gender.GENDER_MAN ? community.price_men : community.price_women;
            AppUtilities.info("JoinAndPay.processFreeOrPayment paymentMethod: " + paymentMethod + " for " + offeringPriceUSD);
            if (paymentMethod == PaymentMethod.APPLE_IAP || paymentMethod == PaymentMethod.GOOGLE_IAP) {

              if (AppUtilities.DEBUGLEVEL.beta) {
                Purchases.setLogLevel(LOG_LEVEL.VERBOSE);
              }

              const rnp = AppUtils.getSimpleGlobalStateTransient<boolean>(AppConsts.SIMPLE_GLOBAL_STATE_TRANSIENT_REACT_NATIVE_PURCHASES_INITIALIZED);
              if (rnp === undefined) {
                AppUtilities.info("JoinAndPay.processFreeOrPayment calling Purchases.configure");
                // https://revenuecat.github.io/react-native-purchases-docs/9.10.0/classes/default.html#configure

                if (paymentMethod == PaymentMethod.APPLE_IAP) {
                  Purchases.configure({ apiKey: Constants.expoConfig?.extra?.revenueCatAppleIAPKey });
                } else if (paymentMethod == PaymentMethod.GOOGLE_IAP) {
                  Purchases.configure({ apiKey: Constants.expoConfig?.extra?.revenueCatGoogleIAPKey });
                }
                AppUtils.setSimpleGlobalStateTransient(AppConsts.SIMPLE_GLOBAL_STATE_TRANSIENT_REACT_NATIVE_PURCHASES_INITIALIZED, true);
              }

              AppUtilities.info("JoinAndPay.processFreeOrPayment calling Purchases.logIn " + uinfo.uniqueid);
              // https://revenuecat.github.io/react-native-purchases-docs/9.10.0/classes/default.html#logIn
              const loginResult : LogInResult = await Purchases.logIn(uinfo.uniqueid);
              AppUtilities.info("JoinAndPay.processFreeOrPayment returned from Purchases.logIn");
              if (AppUtilities.DEBUGLEVEL.beta) {
                AppUtilities.dir(loginResult);
              }

              AppUtilities.info("JoinAndPay.processFreeOrPayment calling Purchases.getOfferings");
              // https://www.revenuecat.com/docs/getting-started/displaying-products
              // https://revenuecat.github.io/react-native-purchases-docs/9.10.0/classes/default.html#getOfferings
              const offerings : PurchasesOfferings = await Purchases.getOfferings();
              AppUtilities.info("JoinAndPay.processFreeOrPayment returned from Purchases.getOfferings");
              if (AppUtilities.DEBUGLEVEL.beta) {
                AppUtilities.dir(offerings.all);
              }

              const searchOfferingName = "offering" + Math.ceil(offeringPriceUSD);
              let offering : PurchasesOffering | null = null;
              for (const offeringName in offerings.all) {
                if (offeringName == searchOfferingName) {
                  offering = offerings.all[offeringName];
                }
              }

              if (offering) {
                AppUtilities.info("JoinAndPay.processFreeOrPayment offering:");
                AppUtilities.dir(offering);

                // This will fire the other useEffect
                setOffering(offering);
              } else {
                throw new Error("Could not find offering for price " + offeringPriceUSD);
              }
            } else if (paymentMethod == PaymentMethod.CREDIT_CARD) {
              if (community.wpe || isDiscountCodeSandbox(verifiedDiscountCode)) {
                // Redirect to CCBill
                if (AppUtilities.isWeb()) {
                  // https://ccbill.com/doc/dynamic-pricing-user-guide

                  const ccredirect: string = ((response2 as any).body as any).ccredirect;

                  AppUtilities.info("JoinAndPay.processFreeOrPayment redirecting to " + ccredirect);

                  setTimeout(
                    async () => {
                      const message = "The credit card processor seems to be slow. Try refreshing.";
                      AppUtilities.info("JoinAndPay.processFreeOrPayment: " + message);
                      AppUtilities.handleError(message, "JoinAndPay.processFreeOrPayment");
                      alert(message);

                      // It doesn't work to try to navigate again
                      //AppUtils.webFullPageNavigate(ccredirect);
                    },
                    15000,
                  );

                  AppUtils.webFullPageNavigate(ccredirect);
                  //AppUtils.webFullPageNavigate("http://localhost:3000/rabbl/api/diagnostics?emulate_host=rabbl.com&sleep=60");

                  // We want to keep the spinner going and the next button
                  // disabled while we do the full page redirect, so
                  // just return here
                  runFinally = false;
                  return;
                } else {
                  throw new Error("Credit card payments in apps aren't available yet");
                }
              } else {
                throw new Error("Web payments aren't enabled yet");
              }

            } else {
              throw new Error("Payment method not implemented: " + paymentMethod);
            }
          }
        }
      } catch (e) {
        humanVerifier?.current?.reset();
        setError(AppUtilities.getErrors(e));
        AppUtils.showErrorToast(AppUtilities.getErrors(e), () => processFreeOrPayment(), true);
        AppUtilities.handleException(e, "JoinAndPay.processFreeOrPayment");
      } finally {
        if (runFinally) {
          setProcessing(false);
          hideSpinner();
        }
      }
    },
    [
      domain,
      user_info,
      gender,
      loggedIn,
      paymentMethod,
      processing,
      creditCard,
      humanVerifierToken,
      verifiedDiscountFree,
      verifiedDiscountCode,
      community,
      invitecode,
    ]
  );

  const onPay = useCallback(async () => {
    try {
      if (offering) {
        // https://revenuecat.github.io/react-native-purchases-docs/9.10.0/classes/default.html#purchasePackage
        // https://www.revenuecat.com/docs/platform-resources/non-subscriptions
        // https://www.revenuecat.com/docs/getting-started/making-purchases
        setProcessing(true);
        showSpinner();

        AppUtilities.info("JoinAndPay.onPay calling Purchases.purchasePackage");
        const purchaseResult : MakePurchaseResult = await Purchases.purchasePackage(offering.availablePackages[0]);
        AppUtilities.info("JoinAndPay.processFreeOrPayment returned from Purchases.purchasePackage");
        if (AppUtilities.DEBUGLEVEL.beta) {
          AppUtilities.dir(purchaseResult);
        }

        // Charge successful, now mark in our database
        const response2 = await AppUtilities.fetchAuthorized(
          "JoinAndPay.onPay",
          "/rabbl/api/finishpay",
          "POST",
          {
            domain: domain,
            transaction: purchaseResult.transaction.transactionIdentifier,
            purchaseResult: purchaseResult,
          }
        );

        dispatch(setData(response2));
        // The dispatch will update communityMembership and there's another useEffect that does a redirect
      }
    } catch (e) {
      setError(AppUtilities.getErrors(e));
      AppUtils.showErrorToast(AppUtilities.getErrors(e), () => onPay(), true);
      AppUtilities.handleException(e, "JoinAndPay.onPay");
    } finally {
      setProcessing(false);
      hideSpinner();
    }
  }, [domain, offering, processing]);

  const isFree = checkFree(community, gender, verifiedDiscountFree);
  let nextDisabled = true;
  if (gender != Gender.GENDER_UNKNOWN) {
    if (isFree) {
      nextDisabled = false;
    } else {
      if (paymentMethod == PaymentMethod.APPLE_IAP ||
          paymentMethod == PaymentMethod.GOOGLE_IAP
      ) {
        nextDisabled = false;
      } else if (paymentMethod == PaymentMethod.CREDIT_CARD) {
        nextDisabled = false;
      }
    }
  }
  if (processing) {
    nextDisabled = true;
  }
  //if (community.inviteonly && invitecode.length == 0) {
  //  nextDisabled = true;
  //}

  return (
    <>
      <Panel variant='raised' elevation={4}>
        <View style={[AppConstants.STYLES.padding20gap20center, { maxWidth: AppUtils.calculateMaxWidth(screenWidth, AppUtils.MaxWidthType.INNER) }]}>
          <CustomText>Join {community.name}</CustomText>

          {
            !loggedIn ?
                buttons()
              :
                <>
                  {
                    (communityMembership.payment_status ?? -1) == PaymentStatus.PAYMENT_STATUS_PENDING && !processing && (
                      <>
                        <CustomDivider fullWidth={true} />
                        <AppText>Having trouble with payment?</AppText>
                        <AppText>Sometimes it takes a minute so try refreshing.</AppText>
                        <Link href="/contact" asChild={true} className={"nontext"}><Button>Get help</Button></Link>
                      </>
                    )
                  }
                  {
                    isFree ?
                        <AppText style={AppConstants.STYLES.subheading1}>Your cover charge is free!</AppText>
                      :
                        <>
                          {
                            community.price_men == community.price_women &&
                              <>
                                <AppText style={AppConstants.STYLES.subheading1}>One-time cover charge</AppText>
                                <AppText style={AppConstants.STYLES.subheading1}>{AppUtils.formatPublicPrice(community.price_men)}</AppText>
                              </>
                          }
                        </>
                  }
                  <CustomDivider fullWidth={true} />

                  { !offering ?
                      (
                        <>
                          {
                            !AppUtils.isValidUserInfoGender(user_info) && 
                              <>
                                <Fieldset label='You are a' style={AppConstants.STYLES.gap20}>
                                  <Radio
                                    status={gender == Gender.GENDER_MAN ? 'checked' : 'unchecked'}
                                    onPress={() => setGender(Gender.GENDER_MAN)}
                                    label='Man'
                                  />
                                  <Radio
                                    status={gender == Gender.GENDER_WOMAN ? 'checked' : 'unchecked'}
                                    onPress={() => setGender(Gender.GENDER_WOMAN)}
                                    label='Woman'
                                  />
                                </Fieldset>
                              </>
                          }

                          {
                            gender != Gender.GENDER_UNKNOWN && !isFree && (
                              <>
                                {
                                  !isFree && (
                                    <AppText style={AppConstants.STYLES.subheading1}>One-time cover charge</AppText>
                                  )
                                }
                                <CustomText type={'large'}>{AppUtils.formatPublicPrice(gender == Gender.GENDER_MAN ? community.price_men : community.price_women)}</CustomText>
                                <Discount
                                  gender={gender}
                                  onSuccess={( code, newpricefree ) => {
                                    setVerifiedDiscountCode(code);
                                    setVerifiedDiscountFree(newpricefree);
                                    setJoinErrors([]);
                                  }}
                                />

                                {
                                  isDiscountCodeSandbox(verifiedDiscountCode) && (
                                    <CustomText>Sandbox mode</CustomText>
                                  )
                                }

                                <CustomDivider fullWidth={true} />
                                
                                <Fieldset label='Payment method' style={AppConstants.STYLES.gap20}>
                                  {
                                    AppUtilities.isAndroid() &&
                                      <Radio
                                        status={paymentMethod == PaymentMethod.GOOGLE_IAP ? 'checked' : 'unchecked'}
                                        onPress={() => setPaymentMethod(PaymentMethod.GOOGLE_IAP)}
                                        label='Google Play In-App Purchase'
                                      />
                                  }
                                  {
                                    AppUtilities.isIOS() &&
                                      <Radio
                                        status={paymentMethod == PaymentMethod.APPLE_IAP ? 'checked' : 'unchecked'}
                                        onPress={() => setPaymentMethod(PaymentMethod.APPLE_IAP)}
                                        label='Apple App Store In-App Purchase'
                                      />
                                  }
                                  {
                                    AppUtilities.isWeb() &&
                                      <Radio
                                        status={paymentMethod == PaymentMethod.CREDIT_CARD ? 'checked' : 'unchecked'}
                                        onPress={() => setPaymentMethod(PaymentMethod.CREDIT_CARD)}
                                        label='Credit Card'
                                      />
                                  }
                                </Fieldset>
                                {/*
                                  paymentMethod == PaymentMethod.CREDIT_CARD && (
                                    <>
                                      <Fieldset label='Credit card' style={AppConstants.STYLES.gap20}>
                                        <CreditCardInput
                                          onChange={setCreditCard}
                                          labels={{
                                            number: "Credit card number",
                                            expiry: "Expiration Month/Year",
                                            cvc: "CVC/CVV",
                                          }}
                                          placeholderColor="#666666"
                                        />
                                      </Fieldset>
                                      <View style={AppConstants.STYLES.padding2} />
                                      {
                                        NEEDS_BILLING && 
                                          <AddressForm />
                                      }
                                    </>
                                  )
                                */}
                              </>
                            )
                          }
                        </>
                      )
                    :
                      (
                        <>
                          {/*<AppText style={AppConstants.STYLES.heading1}>{offering.availablePackages[0].product.priceString}</AppText>*/}
                          <CustomText type={'large'}>{offering.availablePackages[0].product.priceString}</CustomText>
                        </>
                      )
                  }

                  { USE_VERIFIER && 
                      <View style={AppConstants.STYLES.alignCenter}>
                        <HumanVerifier onSuccess={setHumanVerifierToken} ref={humanVerifier} />
                      </View>
                  }

                  { gender != Gender.GENDER_UNKNOWN &&
                      <>

                        { community.inviteonly && (
                            <>
                              <AppText>This Rabbl is invite-only:</AppText>
                              <TextInput
                                value={invitecode}
                                onChangeText={setInvitecode}
                                placeholder="Invite code"
                                returnKeyType="next"
                                autoComplete="off"
                                style={styles.codeInput}
                              />
                            </>
                        )}
                      </>
                  }

                  {
                    joinErrors.length == 1 && joinErrors[0] == CREDIT_CARD_INPUT_NOT_AVAILABLE ?
                        <View style={styles.simpleWrapper}>
                          <AppText key={"error0"} style={AppConstants.STYLES.fontBoldColorRed}>Error: Web payments not yet available.</AppText>
                          <AppText>{AppConsts.PAYMENTS_LINE1}</AppText>
                          <View style={AppConstants.STYLES.horizontal1Wrap}>
                            <Link href="/joinapple" asChild={AppUtilities.isWeb()}>
                              <Image source={require('@/assets/images/testflight174x59.png')} style={styles.appStoreImage} />
                            </Link>
                            <ExternalLink href={AppConsts.ANDROID_STORE_LINK} target='_blank' asChild={AppUtilities.isWeb()}>
                              <Image source={require('@/assets/images/playstore174x52.png')} style={styles.playStoreImage} />
                            </ExternalLink>
                          </View>
                          <AppText>After you've joined through the Apple or Android app, you can uninstall the app if you want and then just use the website.</AppText>
                        </View>
                      :
                        joinErrors.map((e, index) =>
                          <AppText key={"error" + index} style={AppConstants.STYLES.fontBoldColorRed}>{e}</AppText>
                        )
                  }

                  {
                    offering && (
                      <>
                        <AppText>Click Pay to process your payment.</AppText>
                      </>
                    )
                  }

                  <Button
                    onPress={offering ? onPay : processFreeOrPayment}
                    disabled={nextDisabled}
                  >{offering ? "Pay" : "Next"}</Button>

                  { error && error.length > 0 ? (
                    <>
                      <View style={AppConstants.STYLES.padding1} />
                      <CustomDivider fullWidth={true} />
                      <CustomText>Error</CustomText>
                      <AppText>{error}</AppText>
                      <CustomDivider fullWidth={true} />
                    </>
                  ) : null}

                  {/*
                    !isFree &&
                      <>
                        <View style={AppConstants.STYLES.padding2} />
                        <AppText>There are <Link href="/refunds" style={CustomTextStyles.link} target='_blank'>no refunds</Link>.</AppText>
                      </>
                  */}

                </>
          }
        </View>
      </Panel>
      <View style={AppConstants.STYLES.padding10} />
      <CustomDivider fullWidth={true} />
      {
        loggedIn ?
            <SubdomainHomepage joinLoginLinksButtons={false} />
          :
            <SubdomainHomepage buttons={buttons} />
      }
    </>
  );
};
